/**
 * A video that loops silently as ambient motion until someone asks for it,
 * then restarts from the beginning with sound.
 *
 * Two files can be supplied — a wide one and an upright one — and both are in
 * the markup so the correct crop is chosen by CSS rather than by guessing at a
 * breakpoint in script. The one that is not showing is unloaded here, so the
 * visitor only ever downloads the video they can actually see.
 *
 * Autoplay is only permitted while muted. Pressing play is a user gesture, so
 * that is the moment sound can be turned on; the clip is rewound at the same
 * time, because the ambient loop has usually wandered into the middle by then
 * and starting there is not what "play" means.
 */
class NoriVideo extends HTMLElement {
  /** @type {HTMLVideoElement[]} */
  #videos = [];
  /** @type {HTMLVideoElement | null} */
  #active = null;
  /** @type {HTMLButtonElement | null} */
  #play = null;

  connectedCallback() {
    this.#videos = [...this.querySelectorAll('video')];
    this.#play = this.querySelector('[data-video-play]');
    if (this.#videos.length === 0) return;

    this.#selectActive();

    // The crop can change if the device is rotated or the window resized past
    // the breakpoint, so the choice is re-made rather than fixed at load.
    this.#media = matchMedia('(max-width: 749px)');
    this.#media.addEventListener('change', this.#onBreakpoint);

    this.#play?.addEventListener('click', this.#activate);
    this.addEventListener('click', this.#onFrameClick);
  }

  disconnectedCallback() {
    this.#media?.removeEventListener('change', this.#onBreakpoint);
    this.#play?.removeEventListener('click', this.#activate);
    this.removeEventListener('click', this.#onFrameClick);
  }

  /** @type {MediaQueryList | undefined} */
  #media;

  #onBreakpoint = () => {
    const wasPlaying = this.hasAttribute('data-playing');
    this.#selectActive();
    if (wasPlaying) this.#activate();
  };

  #selectActive() {
    // offsetParent is null for a display:none element, which is exactly the
    // question being asked: which of the two is the browser actually showing?
    const visible = this.#videos.find((video) => video.offsetParent !== null) ?? this.#videos[0];
    this.#active = visible;

    for (const video of this.#videos) {
      if (video === visible) {
        if (!video.getAttribute('src') && video.dataset.src) {
          video.setAttribute('src', video.dataset.src);
          video.load();
        }
        video.play().catch(() => {
          // Autoplay can still be refused (data saver, a per-site block). The
          // poster and the play button remain, so there is nothing to repair.
        });
      } else {
        // Unloaded so the hidden crop is never fetched.
        video.pause();
        if (video.getAttribute('src')) {
          video.dataset.src = video.getAttribute('src');
          video.removeAttribute('src');
          video.load();
        }
      }
    }
  }

  #activate = (event) => {
    event?.preventDefault?.();
    const video = this.#active;
    if (!video) return;

    video.muted = false;
    video.loop = false;
    video.controls = true;
    video.currentTime = 0;
    video.play().catch(() => {});

    this.setAttribute('data-playing', '');
  };

  /**
   * Once it is a real player the native controls own the surface; before that,
   * the whole frame is the play target so the small button is not the only
   * thing worth aiming at.
   */
  #onFrameClick = (event) => {
    if (this.hasAttribute('data-playing')) return;
    if (event.target instanceof Element && event.target.closest('[data-video-play]')) return;
    this.#activate(event);
  };
}

if (!customElements.get('nori-video')) {
  customElements.define('nori-video', NoriVideo);
}
